import React, { useState, useEffect } from 'react';
import { Shield, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const COOKIE_CONSENT_KEY = 'cookie_consent';
const COOKIE_PREFS_KEY = 'cookie_preferences';

const defaultPrefs = {
  essenciais: true,
  analiticos: false,
  marketing: false,
};

export default function CookieConsent() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);

  // Hide cookie banner on admin routes — admins consent via the platform
  // T&C separately and the banner overlays interactive areas of the panel.
  const isAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (isAdminRoute) {
      setVisible(false);
      return;
    }
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay so it doesn't flash on page load
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [isAdminRoute]);

  const saveConsent = (preferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_PREFS_KEY, JSON.stringify(preferences));
    setVisible(false);
  };

  const handleAcceptAll = () => {
    saveConsent({ essenciais: true, analiticos: true, marketing: true });
  };

  const handleRejectNonEssential = () => {
    saveConsent({ essenciais: true, analiticos: false, marketing: false });
  };

  const handleSavePreferences = () => {
    saveConsent(prefs);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] animate-in slide-in-from-bottom duration-500"
      data-testid="cookie-consent-banner"
    >
      <div className="bg-white border-t-2 border-[var(--primary)] shadow-[0_-4px_20px_rgba(0,0,0,0.12)]">
        <div className="max-w-6xl mx-auto px-4 py-5 sm:px-6">
          {/* Main content */}
          <div className="flex flex-col lg:flex-row lg:items-start gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="p-2 bg-[var(--primary)]/10 rounded-lg flex-shrink-0 mt-0.5">
                <Shield className="w-5 h-5 text-[var(--primary)]" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-sm mb-1">Sua privacidade importa</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Utilizamos cookies para melhorar sua experiencia na plataforma. Voce pode personalizar suas preferencias ou aceitar todos os cookies.{' '}
                  <Link to="/politica-de-privacidade" className="text-[var(--primary)] hover:underline font-medium">
                    Saiba mais
                  </Link>
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 lg:flex-shrink-0">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="px-4 py-2.5 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-1.5"
                data-testid="cookie-customize-btn"
              >
                Personalizar
                {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              <button
                onClick={handleRejectNonEssential}
                className="px-4 py-2.5 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                data-testid="cookie-reject-btn"
              >
                Rejeitar opcionais
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-5 py-2.5 text-sm font-semibold bg-[var(--primary)] text-white rounded-lg hover:bg-[#450066] transition-colors"
                data-testid="cookie-accept-all-btn"
              >
                Aceitar todos
              </button>
            </div>
          </div>

          {/* Category details */}
          {showDetails && (
            <div className="mt-4 pt-4 border-t border-gray-100 space-y-3" data-testid="cookie-details-panel">
              {/* Essenciais */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium">Cookies Essenciais</p>
                  <p className="text-xs text-gray-500">Necessarios para o funcionamento basico. Nao podem ser desativados.</p>
                </div>
                <div className="w-10 h-6 bg-[var(--primary)] rounded-full relative cursor-not-allowed opacity-70">
                  <div className="absolute right-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow" />
                </div>
              </div>

              {/* Analiticos */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium">Cookies Analiticos</p>
                  <p className="text-xs text-gray-500">Nos ajudam a entender como os visitantes usam a plataforma.</p>
                </div>
                <button
                  onClick={() => setPrefs(p => ({ ...p, analiticos: !p.analiticos }))}
                  className={`w-10 h-6 rounded-full relative transition-colors ${prefs.analiticos ? 'bg-[var(--primary)]' : 'bg-gray-300'}`}
                  data-testid="cookie-toggle-analiticos"
                >
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${prefs.analiticos ? 'right-0.5' : 'left-0.5'}`} />
                </button>
              </div>

              {/* Marketing */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium">Cookies de Marketing</p>
                  <p className="text-xs text-gray-500">Utilizados para exibir conteudo e anuncios relevantes.</p>
                </div>
                <button
                  onClick={() => setPrefs(p => ({ ...p, marketing: !p.marketing }))}
                  className={`w-10 h-6 rounded-full relative transition-colors ${prefs.marketing ? 'bg-[var(--primary)]' : 'bg-gray-300'}`}
                  data-testid="cookie-toggle-marketing"
                >
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${prefs.marketing ? 'right-0.5' : 'left-0.5'}`} />
                </button>
              </div>

              <button
                onClick={handleSavePreferences}
                className="w-full py-2.5 text-sm font-semibold bg-[var(--primary)] text-white rounded-lg hover:bg-[#450066] transition-colors"
                data-testid="cookie-save-prefs-btn"
              >
                Salvar preferencias
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
